import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const PRESET_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#78716c', '#64748b', '#0291EF',
];

export default function ColorPicker({ value, onChange, label, required, error }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <Wrapper ref={ref}>
      {label && (
        <Label $error={!!error} $active={open}>
          {label}
          {required && <span style={{ color: '#E53935', marginLeft: 2 }}>*</span>}
        </Label>
      )}
      <Trigger onClick={() => setOpen(!open)} $error={!!error} $active={open}>
        <Swatch $color={value || '#ccc'} />
        <HexText>{value || ''}</HexText>
      </Trigger>
      {open && (
        <Dropdown>
          <Grid>
            {PRESET_COLORS.map((c) => (
              <ColorBtn
                key={c}
                $color={c}
                $selected={value === c}
                onClick={() => { onChange(c); setOpen(false); }}
              />
            ))}
          </Grid>
          <CustomRow>
            <CustomInput
              type="text"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder="#hex"
              maxLength={7}
            />
            <NativeInput
              type="color"
              value={value || '#0291EF'}
              onChange={(e) => onChange(e.target.value)}
            />
          </CustomRow>
        </Dropdown>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Label = styled.label`
  position: absolute;
  top: -8px;
  left: 12px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $error, $active }) => $error ? '#E53935' : $active ? '#0291EF' : '#A0A8B4'};
  background: #fff;
  padding: 0 4px;
  z-index: 1;
  pointer-events: none;
`;

const Trigger = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 14px;
  border: 1.5px solid ${({ $error, $active }) => $error ? '#E53935' : $active ? '#0291EF' : '#E0E4E8'};
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  &:hover { border-color: ${({ $error, $active }) => $error ? '#E53935' : $active ? '#0291EF' : '#C0C8D0'}; }
`;

const Swatch = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
  border: 1px solid rgba(0,0,0,0.1);
  flex-shrink: 0;
`;

const HexText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #004242;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 66, 66, 0.12);
  padding: 12px;
  z-index: 100;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 6px;
  margin-bottom: 10px;
`;

const ColorBtn = styled.button`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 4px;
  border: 2px solid ${({ $selected }) => $selected ? '#004242' : 'transparent'};
  background: ${({ $color }) => $color};
  cursor: pointer;
  &:hover { transform: scale(1.15); }
`;

const CustomRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CustomInput = styled.input`
  flex: 1;
  height: 32px;
  padding: 0 10px;
  border: 1.5px solid #E0E4E8;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #004242;
  outline: none;
  &:focus { border-color: #0291EF; }
`;

const NativeInput = styled.input`
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  cursor: pointer;
  &::-webkit-color-swatch-wrapper { padding: 2px; }
  &::-webkit-color-swatch { border: none; border-radius: 2px; }
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-family: 'Nunito Sans', sans-serif;
  color: #E53935;
`;
